import * as R from "ramda";
import React from "react";
import styled from "styled-components";
import { Modal, List, Avatar, Radio, Empty, Icon } from "antd";
import { useTranslation } from "react-i18next";

import Button from "./Button";

const StyledModal = styled(Modal)`
  .ant-radio-group {
    display: block;
  }

  .ant-list-item {
    border: 1px solid rgba(0, 0, 0, 0.1) !important;
    border-radius: 4px;
    padding: 12px 16px;
    margin-bottom: 8px;

    .ant-list-item-meta {
      align-items: center;
    }

    &.selected {
      border-color: ${({ theme }) => theme["@primary"]} !important;
    }
  }
`;

const NoPages = styled(Empty)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;

  .ant-empty-image {
    height: auto;
  }

  .ant-empty-description {
    max-width: 360px;
  }

  .anticon {
    font-size: 48px;
    color: rgba(0, 0, 0, 0.2);
  }
`;

export default function SelectFacebookPageModal({
  accessToken,
  onSelect,
  onStartLoading,
  onEndLoading
}) {
  const { t } = useTranslation(["global"]);

  const [showModal, setShowModal] = React.useState(false);
  const [pages, setPages] = React.useState([]);
  const [selectedPageId, setSelectedPageId] = React.useState();

  const confirmPage = React.useCallback(
    page => {
      onSelect(page);
      setShowModal(false);
    },
    [onSelect]
  );

  React.useEffect(() => {
    if (accessToken) {
      onStartLoading();
      window.FB.api(
        "/me/accounts",
        "GET",
        {
          fields: "access_token,name,picture"
        },
        ({ data }) => {
          onEndLoading();

          // If there is just one page with permissions select it automatically
          if (data.length === 1) {
            const page = data[0];
            confirmPage(page);
          } else {
            setPages(data);
            data.length && setSelectedPageId(data[0].id);
            setShowModal(true);
          }
        }
      );
    }
  }, [accessToken, confirmPage, onStartLoading, onEndLoading]);

  return (
    <StyledModal
      visible={showModal}
      title={t("company:socialMedia.facebook.selectPage")}
      footer={
        pages.length
          ? [
              <Button
                onClick={() => {
                  const selectedPage = R.find(
                    R.propEq("id", selectedPageId),
                    pages
                  );
                  confirmPage(selectedPage);
                }}
                type="primary"
              >
                {t("global:accept")}
              </Button>
            ]
          : null
      }
      onCancel={() => setShowModal(false)}
    >
      {pages.length ? (
        <Radio.Group
          value={selectedPageId}
          onChange={event => setSelectedPageId(event.target.value)}
        >
          <List
            dataSource={pages}
            renderItem={page => (
              <List.Item
                key={page.id}
                actions={[<Radio value={page.id} />]}
                className={selectedPageId === page.id ? "selected" : ""}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      shape="square"
                      icon="shop"
                      src={R.path(["picture", "data", "url"], page)}
                    />
                  }
                  title={page.name}
                />
              </List.Item>
            )}
          />
        </Radio.Group>
      ) : (
        <NoPages
          description={t("company:socialMedia.facebook.noPages")}
          image={<Icon type="warning" />}
        ></NoPages>
      )}
    </StyledModal>
  );
}
